<template>
  <div class="view-container">
    <!-- 搜索面板 -->
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-bullhorn"></i> 标题/内容</label>
          <input v-model="searchForm.keyword" type="text" placeholder="搜索标题或内容" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-tag"></i> 公告类型</label>
          <select v-model="searchForm.types" class="search-select">
            <option value="">全部类型</option>
            <option v-for="t in announcementTypes" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="search-item">
          <label><i class="fas fa-toggle-on"></i> 发布状态</label>
          <select v-model="searchForm.isPublic" class="search-select">
            <option value="">全部</option>
            <option value="1">已发布</option>
            <option value="0">未发布</option>
          </select>
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
          <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
        </div>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-wrapper">
      <div class="table-header-row">
        <button @click="openAddDialog" class="btn btn-primary btn-sm"><i class="fas fa-plus"></i> 新增公告</button>
        <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn"><i class="fas fa-columns"></i> 列设置</button>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th width="60">序号</th>
              <th width="80">类型</th>
              <th width="200">公告标题</th>
              <th width="80">发布人</th>
              <th width="100">部门</th>
              <th width="80">发布状态</th>
              <th width="60">有效</th>
              <th width="120">发布时间</th>
              <th width="120">创建时间</th>
              <th class="action-column" width="160">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in paginatedData" :key="item.Guid">
              <td>{{ (currentPage - 1) * pageSize + idx + 1 }}</td>
              <td><span class="type-badge" :class="'type-' + getTypeCode(item.Types)">{{ item.Types || '-' }}</span></td>
              <td class="title-cell">{{ item.Titles || '-' }}</td>
              <td>{{ item.PublicAccount || '-' }}</td>
              <td>{{ item.PublicDspName || '-' }}</td>
              <td><span class="status-badge" :class="item.IsPublic === 1 ? 'enabled' : 'disabled'">{{ item.IsPublic === 1 ? '已发布' : '草稿' }}</span></td>
              <td><span class="status-badge" :class="item.Active === 1 ? 'enabled' : 'disabled'">{{ item.Active === 1 ? '有效' : '失效' }}</span></td>
              <td>{{ formatTime(item.PublicTime) }}</td>
              <td>{{ formatTime(item.CreateTime) }}</td>
              <td class="action-column">
                <button @click="openDetailDialog(item)" class="btn btn-info btn-xs" title="查看详情"><i class="fas fa-eye"></i></button>
                <button @click="openEditDialog(item)" class="btn btn-primary btn-xs" title="修改"><i class="fas fa-edit"></i></button>
                <button v-if="item.IsPublic !== 1" @click="handlePublish(item)" class="btn btn-success btn-xs" title="发布"><i class="fas fa-paper-plane"></i></button>
                <button v-else @click="handleUnpublish(item)" class="btn btn-warning btn-xs" title="取消发布"><i class="fas fa-undo"></i></button>
                <button @click="openPermDialog(item)" class="btn btn-xs btn-perm" title="权限"><i class="fas fa-shield-alt"></i></button>
                <button @click="handleDelete(item)" class="btn btn-danger btn-xs" title="删除"><i class="fas fa-trash"></i></button>
              </td>
            </tr>
            <tr v-if="paginatedData.length === 0"><td colspan="10" class="empty-cell">暂无公告数据</td></tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">共 {{ totalCount }} 条记录</div>
        <div class="pagination-controls">
          <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control"><option :value="20">20</option><option :value="50">50</option><option :value="75">75</option><option :value="100">100</option></select><span>条</span></div>
          <button @click="prevPage" :disabled="currentPage === 1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
          <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn', { active: currentPage === page, disabled: page === '...' }]" :disabled="page === '...'">{{ page }}</button></div>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
          <div class="page-jump"><span>跳转到</span><input type="number" v-model="jumpPage" min="1" :max="totalPages" class="jump-input" @keyup.enter="handleJump"><span>页</span><button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button></div>
        </div>
      </div>
    </div>

    <!-- 列设置弹窗 -->
    <div v-if="showColumnSelector" class="modal-overlay" @click.self="toggleColumnSelector">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h4>列设置</h4><button @click="toggleColumnSelector" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <label v-for="col in allColumns" :key="col.key" class="column-checkbox"><input type="checkbox" v-model="col.visible" @change="updateVisibleColumns"><span>{{ col.label }}</span></label>
        </div>
        <div class="modal-footer"><button @click="resetColumns" class="btn btn-default">重置</button><button @click="toggleColumnSelector" class="btn btn-primary">确定</button></div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-container modal-lg">
        <div class="modal-header"><h3>{{ formTitle }}</h3><button @click="closeFormModal" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group" style="flex:1"><label>公告类型 *</label><select v-model="form.Types" class="form-control"><option v-for="t in announcementTypes" :key="t" :value="t">{{ t }}</option></select></div>
            <div class="form-group" style="flex:1"><label>发布时间</label><input v-model="form.PublicTime" type="datetime-local" class="form-control"></div>
          </div>
          <div class="form-group"><label>公告标题 *</label><input v-model="form.Titles" type="text" class="form-control" placeholder="请输入公告标题"></div>
          <div class="form-row">
            <div class="form-group" style="flex:1">
              <label>发布人</label>
              <div class="picker-wrapper" @click="showPublisherSelector = true">
                <i class="fas fa-user-circle picker-icon"></i>
                <span class="picker-text" :class="{ placeholder: !form.PublicAccountName }">{{ form.PublicAccountName || '点击选择发布人' }}</span>
                <i class="fas fa-chevron-down picker-arrow"></i>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label>部门</label>
              <div class="picker-wrapper" @click="showDeptSelector = true">
                <i class="fas fa-building picker-icon"></i>
                <span class="picker-text" :class="{ placeholder: !form.PublicDspName }">{{ form.PublicDspName || '点击选择部门' }}</span>
                <i class="fas fa-chevron-down picker-arrow"></i>
              </div>
            </div>
          </div>
          <div class="form-group"><label>公告内容 *</label>
            <div class="editor-wrapper">
              <div class="editor-toolbar">
                <select class="tb-font-family" @change="execCmd('fontName', $event.target.value)" title="字体">
                  <option value="">字体</option>
                  <option value="SimSun">宋体</option>
                  <option value="SimHei">黑体</option>
                  <option value="Microsoft YaHei">微软雅黑</option>
                  <option value="KaiTi">楷体</option>
                  <option value="FangSong">仿宋</option>
                  <option value="Arial">Arial</option>
                </select>
                <select class="tb-font-size" @change="execCmd('fontSize', $event.target.value)" title="字号">
                  <option value="">字号</option>
                  <option value="1">小号</option>
                  <option value="2">较小</option>
                  <option value="3">正常</option>
                  <option value="4">较大</option>
                  <option value="5">大号</option>
                  <option value="6">特大</option>
                  <option value="7">最大</option>
                </select>
                <span class="tb-sep"></span>
                <button class="tb-btn" title="加粗" @click="execCmd('bold')"><b>B</b></button>
                <button class="tb-btn" title="斜体" @click="execCmd('italic')"><i>I</i></button>
                <button class="tb-btn" title="下划线" @click="execCmd('underline')"><u>U</u></button>
                <button class="tb-btn" title="删除线" @click="execCmd('strikeThrough')"><s>S</s></button>
                <span class="tb-sep"></span>
                <input type="color" class="tb-color-picker" @change="execCmd('foreColor', $event.target.value)" title="文字颜色">
                <input type="color" class="tb-color-picker" @change="execCmd('hiliteColor', $event.target.value)" title="背景颜色" value="#ffff00">
                <span class="tb-sep"></span>
                <button class="tb-btn" title="标题1" @click="execCmd('formatBlock', 'h2')">H1</button>
                <button class="tb-btn" title="标题2" @click="execCmd('formatBlock', 'h3')">H2</button>
                <span class="tb-sep"></span>
                <button class="tb-btn" title="无序列表" @click="execCmd('insertUnorderedList')"><i class="fas fa-list-ul"></i></button>
                <button class="tb-btn" title="有序列表" @click="execCmd('insertOrderedList')"><i class="fas fa-list-ol"></i></button>
                <button class="tb-btn" title="减少缩进" @click="execCmd('outdent')"><i class="fas fa-outdent"></i></button>
                <button class="tb-btn" title="增加缩进" @click="execCmd('indent')"><i class="fas fa-indent"></i></button>
                <span class="tb-sep"></span>
                <button class="tb-btn" title="左对齐" @click="execCmd('justifyLeft')"><i class="fas fa-align-left"></i></button>
                <button class="tb-btn" title="居中" @click="execCmd('justifyCenter')"><i class="fas fa-align-center"></i></button>
                <button class="tb-btn" title="右对齐" @click="execCmd('justifyRight')"><i class="fas fa-align-right"></i></button>
                <span class="tb-sep"></span>
                <button class="tb-btn" title="插入表格" @click="openTableDialog"><i class="fas fa-table"></i></button>
                <button class="tb-btn" title="插入链接" @click="openLinkDialog"><i class="fas fa-link"></i></button>
                <button class="tb-btn" title="插入图片" @click="openImageDialog"><i class="fas fa-image"></i></button>
                <button class="tb-btn" title="插入分隔线" @click="execCmd('insertHorizontalRule')"><i class="fas fa-minus"></i></button>
                <button class="tb-btn" title="清除格式" @click="execCmd('removeFormat')"><i class="fas fa-remove-format"></i></button>
              </div>
              <div ref="editorRef" class="editor-content" contenteditable="true" @input="onEditorInput"></div>
            </div>
          </div>
          <div class="form-row"><div class="form-group"><label class="checkbox-label"><input type="checkbox" v-model="form.Active" :true-value="1" :false-value="0"><span>有效</span></label></div></div>
        </div>
        <div class="modal-footer"><button @click="closeFormModal" class="btn btn-default">取消</button><button @click="saveForm" class="btn btn-primary" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button></div>
      </div>
    </div>

    <!-- 查看详情弹窗 -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="showDetailModal = false">
      <div class="modal-container modal-lg">
        <div class="modal-header"><h3>公告详情</h3><button @click="showDetailModal = false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="detail-title-section">
            <h2 class="detail-view-title">{{ detailItem.Titles }}</h2>
            <div class="detail-meta">
              <span class="type-badge" :class="'type-' + getTypeCode(detailItem.Types)">{{ detailItem.Types }}</span>
              <span v-if="detailItem.PublicAccount">发布人：{{ detailItem.PublicAccount }}</span>
              <span v-if="detailItem.PublicDspName">部门：{{ detailItem.PublicDspName }}</span>
              <span v-if="detailItem.PublicTime">发布时间：{{ formatTime(detailItem.PublicTime) }}</span>
              <span class="status-badge" :class="detailItem.IsPublic === 1 ? 'enabled' : 'disabled'">{{ detailItem.IsPublic === 1 ? '已发布' : '草稿' }}</span>
            </div>
          </div>
          <div class="detail-divider"></div>
          <div class="detail-content" v-html="detailItem.Context"></div>
        </div>
        <div class="modal-footer"><button @click="showDetailModal = false" class="btn btn-default">关闭</button></div>
      </div>
    </div>

    <!-- 权限管理弹窗 -->
    <div v-if="showPermModal" class="modal-overlay" @click.self="closePermModal">
      <div class="modal-container modal-lg">
        <div class="modal-header">
          <h3><i class="fas fa-shield-alt"></i> 公告权限 - {{ permTarget?.Titles }}</h3>
          <button @click="closePermModal" class="close-btn"><i class="fas fa-times"></i></button>
        </div>
        <div class="modal-body">
          <div class="perm-toolbar">
            <button @click="openPermAddDialog" class="btn btn-primary btn-sm"><i class="fas fa-plus"></i> 添加权限</button>
            <button @click="removePermItem" class="btn btn-danger btn-sm" :disabled="!permList.some(p => p._selected)"><i class="fas fa-trash"></i> 删除选中</button>
            <span class="perm-tip">提示：点击行可选中，支持多选删除</span>
          </div>
          <table class="perm-table">
            <thead>
              <tr>
                <th width="30"><input type="checkbox" v-model="permAllSelected" @change="toggleAllPerm"></th>
                <th width="80">授权类型</th>
                <th>授权对象</th>
                <th width="80">权限范围</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in permList" :key="idx" :class="{ selected: p._selected }" @click="selectPermItem(idx)">
                <td><input type="checkbox" v-model="p._selected" @click.stop></td>
                <td>{{ p.Type === 'User' ? '用户' : p.Type === 'Org' ? '组织' : p.Type === 'Role' ? '角色' : p.Type }}</td>
                <td>{{ p.DisplayValue }}</td>
                <td>
                  <select v-model="p.Permission" class="form-control perm-edit-select" @click.stop>
                    <option value="1">允许</option>
                    <option value="0">拒绝</option>
                  </select>
                </td>
                <td class="action-column">
                  <button @click.stop="removeSinglePerm(idx)" class="btn btn-danger btn-xs" title="删除"><i class="fas fa-trash"></i></button>
                </td>
              </tr>
              <tr v-if="permList.length === 0"><td colspan="5" class="empty-cell">暂未设置权限，点击"添加权限"按钮添加</td></tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button @click="closePermModal" class="btn btn-default">取消</button>
          <button @click="savePermissions" class="btn btn-primary" :disabled="permSaving">{{ permSaving ? '保存中...' : '保存权限' }}</button>
        </div>
      </div>
    </div>

    <!-- 添加权限弹窗 -->
    <div v-if="showPermAddModal" class="modal-overlay" @click.self="showPermAddModal = false">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h3>添加权限</h3><button @click="showPermAddModal = false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-group"><label>授权类型</label><select v-model="permAddForm.Type" class="form-control"><option value="User">用户</option><option value="Org">组织</option><option value="Role">角色</option></select></div>
          <div class="form-group">
            <label>授权对象</label>
            <div class="picker-wrapper" @click="showPermTargetSelector = true">
              <i class="fas fa-user-check picker-icon"></i>
              <span class="picker-text" :class="{ placeholder: !permAddForm.DisplayValue }">{{ permAddForm.DisplayValue || permTargetPlaceholder }}</span>
              <i class="fas fa-chevron-down picker-arrow"></i>
            </div>
          </div>
          <div class="form-group"><label>权限范围</label><select v-model="permAddForm.Permission" class="form-control"><option value="1">允许查看</option><option value="0">拒绝查看</option></select></div>
        </div>
        <div class="modal-footer"><button @click="showPermAddModal = false" class="btn btn-default">取消</button><button @click="addPermItem" class="btn btn-primary">确定</button></div>
      </div>
    </div>

    <!-- 权限授权对象选择器 -->
    <PersonSelector
      :visible="showPermTargetSelector && (permAddForm.Type === 'User' || permAddForm.Type === 'Role')"
      :sourceType="permAddForm.Type === 'Role' ? 'role' : 'user'"
      :modes="[permAddForm.Type === 'Role' ? 'role' : 'user']"
      @close="showPermTargetSelector = false"
      @selected="onPermTargetSelected"
    />
    <TreeSelector
      :visible="showPermTargetSelector && permAddForm.Type === 'Org'"
      title="选择组织"
      search-placeholder="搜索组织..."
      icon="fa-building"
      :nodes="orgTree"
      :selected-ids="selectedPermOrgIds"
      :multiple="false"
      @close="showPermTargetSelector = false"
      @confirm="onPermOrgSelected"
      @update:selected-ids="(ids) => selectedPermOrgIds = ids"
    />

    <!-- 插入表格对话框 -->
    <div v-if="showTableDialog" class="modal-overlay" @click.self="showTableDialog = false">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h3>插入表格</h3><button @click="showTableDialog = false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group" style="flex:1"><label>行数</label><input v-model.number="tableForm.rows" type="number" min="1" max="20" class="form-control"></div>
            <div class="form-group" style="flex:1"><label>列数</label><input v-model.number="tableForm.cols" type="number" min="1" max="15" class="form-control"></div>
          </div>
        </div>
        <div class="modal-footer"><button @click="showTableDialog = false" class="btn btn-default">取消</button><button @click="insertTable" class="btn btn-primary">插入</button></div>
      </div>
    </div>

    <!-- 插入链接对话框 -->
    <div v-if="showLinkDialog" class="modal-overlay" @click.self="showLinkDialog = false">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h3>插入链接</h3><button @click="showLinkDialog = false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-group"><label>链接地址</label><input v-model="linkForm.url" type="url" class="form-control" placeholder="https://..."></div>
          <div class="form-group"><label>显示文本</label><input v-model="linkForm.text" type="text" class="form-control" placeholder="链接文字"></div>
        </div>
        <div class="modal-footer"><button @click="showLinkDialog = false" class="btn btn-default">取消</button><button @click="insertLink" class="btn btn-primary">插入</button></div>
      </div>
    </div>

    <!-- 插入图片对话框 -->
    <div v-if="showImageDialog" class="modal-overlay" @click.self="showImageDialog = false">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h3>插入图片</h3><button @click="showImageDialog = false" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body">
          <div class="form-group"><label>图片地址</label><input v-model="imageForm.url" type="url" class="form-control" placeholder="https://..."></div>
          <div class="form-group"><label>替代文字</label><input v-model="imageForm.alt" type="text" class="form-control" placeholder="图片描述"></div>
          <div class="form-group"><label>宽度（可选）</label><input v-model="imageForm.width" type="text" class="form-control" placeholder="如 600px 或 100%"></div>
        </div>
        <div class="modal-footer"><button @click="showImageDialog = false" class="btn btn-default">取消</button><button @click="insertImage" class="btn btn-primary">插入</button></div>
      </div>
    </div>

    <!-- 发布人选择器 -->
    <PersonSelector
      :visible="showPublisherSelector"
      :modes="['user']"
      @close="showPublisherSelector = false"
      @selected="onPublisherSelected"
    />

    <!-- 部门选择器 -->
    <TreeSelector
      :visible="showDeptSelector"
      title="选择部门"
      search-placeholder="搜索组织..."
      icon="fa-building"
      :nodes="orgTree"
      :selected-ids="selectedDeptIds"
      :multiple="false"
      @close="showDeptSelector = false"
      @confirm="onDeptSelected"
      @update:selected-ids="(ids) => selectedDeptIds = ids"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { getAnnouncementList, createAnnouncement, updateAnnouncement,
  deleteAnnouncement, publishAnnouncement, unpublishAnnouncement,
  getAnnouncementPermissions, saveAnnouncementPermissions
} from '../api/announcement'
import { getOrgTree } from '../api/org'
import PersonSelector from './develop/ProcessDesigner/PersonSelector.vue'
import TreeSelector from '../components/TreeSelector.vue'

const announcementTypes = ['公司新闻', '集团新闻', '通知公告', '部门公告', '系统消息', '管理制度']
const typeCodeMap = { '公司新闻': 'company', '集团新闻': 'group', '通知公告': 'notice', '部门公告': 'dept', '系统消息': 'system', '管理制度': 'policy' }

// ========== 搜索 & 分页 ==========
const currentPage = ref(1)
const pageSize = ref(20)
const jumpPage = ref(1)
const searchForm = reactive({ keyword: '', types: '', isPublic: '' })
const announcements = ref([])

const allColumns = ref([
  { key: 'Types', label: '类型', visible: true },
  { key: 'Titles', label: '公告标题', visible: true },
  { key: 'PublicAccount', label: '发布人', visible: true },
  { key: 'PublicDspName', label: '部门', visible: true },
  { key: 'IsPublic', label: '发布状态', visible: true },
  { key: 'Active', label: '有效', visible: true },
  { key: 'PublicTime', label: '发布时间', visible: true },
  { key: 'CreateTime', label: '创建时间', visible: false },
  { key: 'Creater', label: '创建人', visible: false }
])

const showColumnSelector = ref(false)
function toggleColumnSelector() { showColumnSelector.value = !showColumnSelector.value }
function resetColumns() { allColumns.value.forEach(c => c.visible = true) }
function updateVisibleColumns() {
  if (allColumns.value.filter(c => c.visible).length < 1) {
    const first = allColumns.value.find(c => c.key === 'Titles'); if (first) first.visible = true
  }
}

// ========== 数据加载 ==========
function normalizeRow(r) {
  // .NET8 JSON序列化默认camelCase，但模板用PascalCase属性
  return {
    Guid: r.Guid || r.guid || '',
    Types: r.Types || r.types || '',
    Titles: r.Titles || r.titles || '',
    Context: r.Context || r.context || '',
    IsPublic: r.IsPublic ?? r.isPublic ?? 0,
    PublicTime: r.PublicTime || r.publicTime || '',
    PublicAccount: r.PublicAccount || r.publicAccount || '',
    PublicDspName: r.PublicDspName || r.publicDspName || '',
    Active: r.Active ?? r.active ?? 1,
    CreateTime: r.CreateTime || r.createTime || '',
    Creater: r.Creater || r.creater || ''
  }
}

async function loadData() {
  try {
    const res = await getAnnouncementList(searchForm.keyword || undefined)
    console.log('[公告管理] API原始响应:', res)
    const list = res.Data || res.data || []
    announcements.value = list.map(normalizeRow)
    console.log('[公告管理] 数据列表(条数):', announcements.value.length, announcements.value)
  } catch (e) {
    console.error('加载公告失败', e)
    $message.error('加载公告失败: ' + (e.response?.data?.Message || e.message || ''))
  }
}

onMounted(() => { loadData(); loadOrgTreeData() })

// ========== 过滤排序分页 ==========
const filteredData = computed(() => {
  let result = [...announcements.value]
  if (searchForm.keyword) {
    const kw = searchForm.keyword.toLowerCase()
    result = result.filter(a =>
      (a.Titles && String(a.Titles).toLowerCase().includes(kw)) ||
      (a.Types && String(a.Types).toLowerCase().includes(kw)) ||
      (a.PublicAccount && String(a.PublicAccount).toLowerCase().includes(kw))
    )
  }
  if (searchForm.types) result = result.filter(a => a.Types === searchForm.types)
  if (searchForm.isPublic) result = result.filter(a => a.IsPublic === parseInt(searchForm.isPublic))
  return result
})

const totalCount = computed(() => filteredData.value.length)
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value) || 1)
const paginatedData = computed(() => {
  const s = (currentPage.value - 1) * pageSize.value
  return filteredData.value.slice(s, s + pageSize.value)
})

const visiblePages = computed(() => {
  const pages = [], t = totalPages.value, c = currentPage.value
  if (t <= 7) { for (let i = 1; i <= t; i++) pages.push(i) }
  else { pages.push(1); if (c > 4) pages.push('...'); for (let i = Math.max(2, c - 2); i <= Math.min(t - 1, c + 2); i++) pages.push(i); if (c < t - 3) pages.push('...'); pages.push(t) }
  return pages
})

function handleSearch() { currentPage.value = 1 }
function resetSearch() { searchForm.keyword = ''; searchForm.types = ''; searchForm.isPublic = ''; currentPage.value = 1; loadData() }
function prevPage() { if (currentPage.value > 1) currentPage.value-- }
function nextPage() { if (currentPage.value < totalPages.value) currentPage.value++ }
function goToPage(p) { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }
function handleJump() { const p = parseInt(jumpPage.value); if (p >= 1 && p <= totalPages.value) currentPage.value = p; else jumpPage.value = currentPage.value }

// ========== 工具函数 ==========
function getTypeCode(type) { return typeCodeMap[type] || 'default' }
function formatTime(time) {
  if (!time) return '-'
  // 处理 /Date(1234567890)/ 格式（旧 ASP.NET）
  const match = /\/Date\((\d+)\)\//.exec(time)
  const d = match ? new Date(parseInt(match[1])) : new Date(time)
  if (isNaN(d.getTime())) return time
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`
}

// ========== 新增/编辑 ==========
const showFormModal = ref(false)
const editingGuid = ref(null)
const editorRef = ref(null)
const saving = ref(false)
const formDefault = { Types: '公司新闻', Titles: '', Context: '', PublicTime: '', PublicAccount: '', PublicAccountName: '', PublicDspName: '', Active: 1, IsPublic: 0 }
const form = reactive({ ...formDefault })
const formTitle = computed(() => editingGuid.value ? '修改公告' : '新增公告')

const showPublisherSelector = ref(false)
const showDeptSelector = ref(false)
const orgTree = ref([])
const selectedDeptIds = ref([])

function resetForm() {
  Object.assign(form, formDefault)
  form.PublicTime = formatDateTimeLocal(new Date())
}
function formatDateTimeLocal(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}T${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`
}

function openAddDialog() {
  editingGuid.value = null; resetForm(); showFormModal.value = true
  nextTick(() => { if (editorRef.value) editorRef.value.innerHTML = '' })
}
function openEditDialog(item) {
  editingGuid.value = item.Guid
  form.Types = item.Types || '公司新闻'; form.Titles = item.Titles || ''
  form.PublicAccount = item.PublicAccount || ''; form.PublicDspName = item.PublicDspName || ''
  form.PublicAccountName = item.PublicAccount || ''
  form.IsPublic = item.IsPublic || 0; form.Active = item.Active !== undefined ? item.Active : 1
  form.PublicTime = item.PublicTime ? formatDateTimeLocal(new Date(item.PublicTime)) : ''
  showFormModal.value = true
  nextTick(() => { if (editorRef.value) editorRef.value.innerHTML = item.Context || '' })
}
function closeFormModal() { showFormModal.value = false; editingGuid.value = null; resetForm() }

function mapOrgNode(node) {
  const mapped = {
    ...node,
    id: node.id || node.guid || node.Guid,
    name: node.oUsName || node.OUsName || node.name || node.label || ''
  }
  if (node.children && Array.isArray(node.children)) {
    mapped.children = node.children.map(mapOrgNode)
  }
  return mapped
}
async function loadOrgTreeData() {
  try {
    const res = await getOrgTree()
    orgTree.value = (res.data || res.Data || []).map(mapOrgNode)
  } catch { /* 忽略 */ }
}

function onPublisherSelected(item) {
  form.PublicAccount = item.code || ''
  form.PublicAccountName = `${item.name}（${item.code}）`
  showPublisherSelector.value = false
}

function onDeptSelected() {
  const ids = new Set(selectedDeptIds.value)
  const found = []
  const walk = (nodes) => { for (const n of nodes) { if (ids.has(n.id)) found.push(n); if (n.children) walk(n.children) } }
  walk(orgTree.value)
  form.PublicDspName = found.map(n => n.name).join(', ')
  showDeptSelector.value = false
}

async function saveForm() {
  if (!form.Titles.trim()) { $message.warning('请填写公告标题'); return }
  const htmlContent = editorRef.value ? editorRef.value.innerHTML : ''
  saving.value = true
  const data = { Types: form.Types, Titles: form.Titles.trim(), Context: htmlContent, PublicAccount: form.PublicAccount.trim(), PublicDspName: form.PublicDspName.trim(), Active: form.Active, IsPublic: form.IsPublic, PublicTime: form.PublicTime || formatDateTimeLocal(new Date()) }
  try {
    if (editingGuid.value) await updateAnnouncement(editingGuid.value, data)
    else await createAnnouncement(data)
    closeFormModal(); loadData()
  } catch (e) { $message.error('保存失败: ' + (e.response?.data?.Message || e.message || '')) }
  finally { saving.value = false }
}

// ========== 删除/发布/取消发布 ==========
async function handleDelete(item) {
  if (!await $confirm(`确定删除公告「${item.Titles}」？`)) return
  try { await deleteAnnouncement(item.Guid); $message.success('删除成功'); loadData() } catch (e) { $message.error('删除失败: ' + (e.response?.data?.Message || e.message || '')) }
}
async function handlePublish(item) {
  if (!await $confirm(`确定发布「${item.Titles}」？`)) return
  try { await publishAnnouncement(item.Guid); $message.success('发布成功'); loadData() } catch (e) { $message.error('发布失败: ' + (e.response?.data?.Message || e.message || '')) }
}
async function handleUnpublish(item) {
  if (!await $confirm(`确定取消发布「${item.Titles}」？`)) return
  try { await unpublishAnnouncement(item.Guid); $message.success('已取消发布'); loadData() } catch (e) { $message.error('取消发布失败: ' + (e.response?.data?.Message || e.message || '')) }
}

// ========== 详情 ==========
const showDetailModal = ref(false), detailItem = ref({})
function openDetailDialog(item) { detailItem.value = item; showDetailModal.value = true }

// ========== 富文本 ==========
function execCmd(cmd, val) { document.execCommand(cmd, false, val || null); editorRef.value?.focus() }
function onEditorInput() { form.Context = editorRef.value?.innerHTML || '' }

// 表格对话框
const showTableDialog = ref(false)
const tableForm = reactive({ rows: 3, cols: 3 })
function openTableDialog() { tableForm.rows = 3; tableForm.cols = 3; showTableDialog.value = true }
function insertTable() {
  editorRef.value?.focus()
  const rows = parseInt(tableForm.rows) || 3
  const cols = parseInt(tableForm.cols) || 3
  let html = '<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;width:100%;margin:8px 0">'
  for (let i = 0; i < rows; i++) {
    html += '<tr>'
    for (let j = 0; j < cols; j++) html += '<td style="padding:6px 8px;">&nbsp;</td>'
    html += '</tr>'
  }
  html += '</table>'
  document.execCommand('insertHTML', false, html)
  showTableDialog.value = false
}

// 链接对话框
const showLinkDialog = ref(false)
const linkForm = reactive({ url: '', text: '' })
function openLinkDialog() {
  linkForm.url = 'https://'; linkForm.text = ''
  showLinkDialog.value = true
}
function insertLink() {
  editorRef.value?.focus()
  const text = linkForm.text || linkForm.url
  document.execCommand('createLink', false, linkForm.url)
  if (text && text !== linkForm.url) {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).toString() === linkForm.url) {
      const link = sel.anchorNode?.parentElement
      if (link && link.tagName === 'A') link.textContent = text
    }
  }
  showLinkDialog.value = false
}

// 图片对话框
const showImageDialog = ref(false)
const imageForm = reactive({ url: '', alt: '', width: '' })
function openImageDialog() { imageForm.url = ''; imageForm.alt = ''; imageForm.width = ''; showImageDialog.value = true }
function insertImage() {
  editorRef.value?.focus()
  const widthAttr = imageForm.width ? ` width="${imageForm.width}"` : ''
  const html = `<img src="${imageForm.url}" alt="${imageForm.alt}"${widthAttr} style="max-width:100%">`
  document.execCommand('insertHTML', false, html)
  showImageDialog.value = false
}

// ========== 权限管理 ==========
const showPermModal = ref(false), showPermAddModal = ref(false), permTarget = ref(null)
const permList = ref([]), permSaving = ref(false), permAllSelected = ref(false)
const permAddForm = reactive({ Type: 'User', DisplayValue: '', Value: '', Permission: '1' })
const showPermTargetSelector = ref(false)
const selectedPermOrgIds = ref([])
const permTargetPlaceholder = computed(() => permAddForm.Type === 'User' ? '点击选择用户' : permAddForm.Type === 'Org' ? '点击选择组织' : '点击选择角色')

async function openPermDialog(item) {
  permTarget.value = item; showPermModal.value = true
  try {
    const res = await getAnnouncementPermissions(item.Guid)
    const list = res.Data || res.data || []
    permList.value = list.map(p => ({
      Type: p.Type || p.type || '',
      DisplayValue: p.DisplayValue || p.displayValue || '',
      Value: p.Value || p.value || '',
      Permission: p.Permission || p.permission || '1',
      SysPerm: p.SysPerm ?? p.sysPerm ?? '0',
      _selected: false
    }))
  } catch { permList.value = [] }
}
function closePermModal() { showPermModal.value = false; permTarget.value = null; permList.value = [] }
function selectPermItem(idx) { permList.value.forEach((p, i) => { p._selected = i === idx }) }
function toggleAllPerm() { const val = permAllSelected.value; permList.value.forEach(p => p._selected = val) }
function removePermItem() { permList.value = permList.value.filter(p => !p._selected); permAllSelected.value = false }
function removeSinglePerm(idx) { permList.value.splice(idx, 1) }

function openPermAddDialog() {
  permAddForm.Type = 'User'; permAddForm.DisplayValue = ''; permAddForm.Value = ''; permAddForm.Permission = '1'
  selectedPermOrgIds.value = []
  showPermAddModal.value = true
}
function onPermTargetSelected(item) {
  permAddForm.DisplayValue = `${item.name}（${item.code}）`
  permAddForm.Value = item.code
  showPermTargetSelector.value = false
}
function onPermOrgSelected() {
  const ids = new Set(selectedPermOrgIds.value)
  const found = []
  const walk = (nodes) => { for (const n of nodes) { if (ids.has(n.id)) found.push(n); if (n.children) walk(n.children) } }
  walk(orgTree.value)
  if (found.length > 0) {
    permAddForm.DisplayValue = found[0].name
    permAddForm.Value = found[0].id || found[0].code || ''
  }
  showPermTargetSelector.value = false
}
function addPermItem() {
  if (!permAddForm.DisplayValue.trim()) { $message.warning('请选择授权对象'); return }
  permList.value.push({
    Type: permAddForm.Type, DisplayValue: permAddForm.DisplayValue.trim(),
    Value: permAddForm.Value || permAddForm.DisplayValue.trim(), Permission: permAddForm.Permission,
    SysPerm: '0', _selected: false
  })
  showPermAddModal.value = false
}

async function savePermissions() {
  if (!permTarget.value) return
  permSaving.value = true
  try {
    const data = permList.value.map(({ Type, DisplayValue, Value, Permission, SysPerm }) => ({ Type, DisplayValue, Value, Permission, SysPerm }))
    await saveAnnouncementPermissions(permTarget.value.Guid, data)
    $message.success('权限保存成功'); closePermModal()
  } catch (e) { $message.error('保存权限失败: ' + (e.response?.data?.Message || e.message || '')) }
  finally { permSaving.value = false }
}
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; }

.btn { padding: 6px 15px; border-radius: 4px; border: none; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; gap: 5px; transition: all 0.2s ease; }
.btn-primary { background-color: #1ab394; color: #fff; }
.btn-primary:hover { background-color: #18a689; }
.btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.btn-default:hover { background-color: #e7eaec; }
.btn-info { background-color: #31b0d5; color: #fff; }
.btn-info:hover { background-color: #269abc; }
.btn-warning { background-color: #f0ad4e; color: #fff; }
.btn-warning:hover { background-color: #ec971f; }
.btn-danger { background-color: #d9534f; color: #fff; }
.btn-danger:hover { background-color: #c9302c; }
.btn-success { background-color: #5cb85c; color: #fff; }
.btn-success:hover { background-color: #4cae4c; }
.btn-xs { padding: 3px 8px; font-size: 12px; margin-right: 3px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }
.btn-perm { background-color: #7c3aed; color: #fff; }
.btn-perm:hover { background-color: #6d28d9; }

/* 搜索面板 */
.search-panel { background: #fff; border: 1px solid #e0e4e8; border-radius: 8px; padding: 20px; margin-bottom: 15px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.search-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
.search-item { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.search-item label { font-size: 13px; color: #6b7280; font-weight: 500; }
.search-item label i { margin-right: 2px; color: #1ab394; font-size: 12px; }
.search-input { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 160px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; background: #fafbfc; }
.search-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.search-select { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 130px; outline: none; background: #fafbfc; cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; appearance: auto; }
.search-select:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.search-actions { display: flex; gap: 10px; margin-left: auto; align-self: flex-end; }
.search-btn { padding: 8px 18px; font-size: 13px; border-radius: 6px; font-weight: 500; }

/* 表格 */
.table-wrapper { border: 1px solid #e7eaec; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; flex: 1; min-height: 0; }
.table-header-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background-color: #f9f9f9; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.table-container { overflow-y: auto; flex: 1; min-height: 0; }
.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.data-table th, .data-table td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #e7eaec; white-space: nowrap; font-size: 13px; color: #676a6c; overflow: hidden; text-overflow: ellipsis; }
.data-table th { background-color: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }
.title-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
.empty-cell { text-align: center; padding: 40px; color: #999; }

/* 类型标签 */
.type-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.type-badge.type-company { background: #dbeafe; color: #1d4ed8; }
.type-badge.type-group { background: #e8f4fd; color: #2563eb; }
.type-badge.type-notice { background: #fef9c3; color: #a16207; }
.type-badge.type-dept { background: #dcfce7; color: #16a34a; }
.type-badge.type-system { background: #f3e8ff; color: #7c3aed; }
.type-badge.type-policy { background: #fee2e2; color: #dc2626; }

/* 状态标签 */
.status-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.status-badge.enabled { background: #dcfce7; color: #16a34a; }
.status-badge.disabled { background: #fee2e2; color: #dc2626; }

.action-column { white-space: nowrap; }

/* 分页 */
.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; background-color: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 13px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 10px; }
.page-numbers { display: flex; gap: 5px; }
.page-btn { width: 32px; height: 32px; border: 1px solid #e7eaec; background-color: #fff; color: #676a6c; border-radius: 4px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.page-btn:hover:not(.disabled) { background-color: #f9f9f9; border-color: #1ab394; color: #1ab394; }
.page-btn.active { background-color: #1ab394; border-color: #1ab394; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 5px; margin-right: 10px; padding-right: 10px; border-right: 1px solid #e7eaec; font-size: 13px; }
.page-size-input { width: 60px; padding: 5px 8px; font-size: 13px; }
.page-jump { display: flex; align-items: center; gap: 5px; margin-left: 10px; padding-left: 10px; border-left: 1px solid #e7eaec; }
.page-jump span { font-size: 13px; color: #676a6c; }
.jump-input { width: 50px; padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; text-align: center; }
.jump-input:focus { outline: none; border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,0.2); }
.jump-btn { padding: 5px 12px; }

/* 列设置 */
.column-checkbox { display: flex; align-items: center; gap: 8px; padding: 6px 0; cursor: pointer; font-size: 13px; color: #676a6c; }
.column-settings-btn { padding: 4px 10px; font-size: 12px; }

/* 弹窗通用 */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,0.18); display: flex; flex-direction: column; max-height: 85vh; width: 460px; }
.modal-container.modal-lg { width: 800px; max-width: 90vw; }
.modal-container.modal-sm { width: 420px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.modal-header h3 { margin: 0; font-size: 16px; color: #3a4a63; }
.modal-header h4 { margin: 0; font-size: 14px; color: #1a1a1a; }
.close-btn { background: none; border: none; font-size: 18px; color: #999; cursor: pointer; padding: 4px 8px; border-radius: 4px; }
.close-btn:hover { background: #f0f0f0; color: #333; }
.modal-body { padding: 20px; overflow-y: auto; flex: 1; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

/* 表单 */
.form-group { margin-bottom: 14px; }
.form-group label { display: block; margin-bottom: 4px; font-size: 13px; font-weight: 500; color: #676a6c; }
.form-row { display: flex; gap: 14px; }
.form-control { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; box-sizing: border-box; }
.form-control:focus { outline: none; border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,0.12); }
.checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; }

/* 选择器触发按钮 */
.picker-wrapper { display: flex; align-items: center; width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; background: #fafbfc; cursor: pointer; transition: all 0.2s; box-sizing: border-box; gap: 8px; }
.picker-wrapper:hover { border-color: #1ab394; background: #f0fdf6; }
.picker-wrapper:focus-within { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.picker-icon { color: #1ab394; font-size: 14px; flex-shrink: 0; width: 18px; text-align: center; }
.picker-text { flex: 1; font-size: 13px; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker-text.placeholder { color: #9ca3af; }
.picker-arrow { color: #9ca3af; font-size: 10px; flex-shrink: 0; transition: transform 0.2s; }

/* 富文本 */
.editor-wrapper { border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; }
.editor-toolbar { display: flex; gap: 4px; padding: 6px 8px; background: #f5f5f5; border-bottom: 1px solid #d1d5db; flex-wrap: wrap; }
.tb-btn { padding: 4px 8px; border: 1px solid #e7eaec; background: #fff; border-radius: 3px; cursor: pointer; font-size: 12px; color: #676a6c; }
.tb-btn:hover { background: #e8f4fd; color: #1ab394; }
.tb-sep { width: 1px; background: #e7eaec; }
.tb-font-family { padding: 2px 4px; border: 1px solid #e7eaec; border-radius: 3px; background: #fff; font-size: 12px; color: #676a6c; cursor: pointer; max-width: 90px; }
.tb-font-size { padding: 2px 4px; border: 1px solid #e7eaec; border-radius: 3px; background: #fff; font-size: 12px; color: #676a6c; cursor: pointer; max-width: 64px; }
.tb-color-picker { width: 26px; height: 26px; border: 1px solid #e7eaec; border-radius: 3px; cursor: pointer; padding: 1px; background: #fff; }
.editor-content { min-height: 200px; padding: 12px; font-size: 14px; line-height: 1.6; outline: none; }

/* 详情 */
.detail-title-section { margin-bottom: 16px; }
.detail-view-title { font-size: 20px; color: #1a1a1a; margin: 0 0 12px 0; }
.detail-meta { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.detail-meta span { font-size: 13px; color: #676a6c; }
.detail-divider { border-top: 1px solid #e7eaec; margin: 16px 0; }
.detail-content { font-size: 14px; line-height: 1.8; color: #333; }

/* 权限弹窗 */
.perm-toolbar { display: flex; align-items: center; gap: 8px; padding-bottom: 12px; border-bottom: 1px solid #e7eaec; margin-bottom: 12px; }
.perm-tip { font-size: 12px; color: #999; margin-left: auto; }
.perm-table { width: 100%; border-collapse: collapse; }
.perm-table th, .perm-table td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e7eaec; font-size: 13px; color: #676a6c; }
.perm-table th { background: #f5f5f5; font-weight: 600; }
.perm-table tbody tr:hover { background: #f9fafb; }
.perm-table tbody tr.selected { background: #e8f4fd; }
.perm-edit-select { padding: 4px 6px; font-size: 12px; border: 1px solid #d1d5db; border-radius: 3px; width: 80px; }
.perm-edit-input { padding: 4px 6px; font-size: 12px; border: 1px solid #d1d5db; border-radius: 3px; width: 100%; }
</style>
